import type { Geometry } from "@/types/api-v2";

/** Карта слушает это событие и сразу рисует сохранённый контур. */
export const GEOMETRY_COMMITTED_EVENT = "gpml-geometry-committed";

/** Имя Cache Storage старых сборок PWA (раньше MVT лежал там неделю). */
export const MVT_RUNTIME_CACHE_NAME = "mvt-tiles-cache";

export type CommittedGeometryDetail = {
  objectId: number;
  geometry: Geometry;
};

export function geometriesListQueryKey(objectId: number) {
  return ["objects", objectId, "geometries"] as const;
}

export function publishCommittedGeometry(detail: CommittedGeometryDetail): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(GEOMETRY_COMMITTED_EVENT, { detail }));
}

export function sameEntityId(left: unknown, right: unknown): boolean {
  if (left == null || right == null || left === "" || right === "") return false;
  const a = typeof left === "number" ? left : Number(left);
  const b = typeof right === "number" ? right : Number(right);
  return Number.isFinite(a) && Number.isFinite(b) && a === b;
}

function geoJsonKey(value: unknown): string {
  try {
    return JSON.stringify(value ?? null);
  } catch {
    return "";
  }
}

function timestamp(value: string | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Пока список с сервера не содержит сохранённый контур, рисуем ответ сохранения.
 * Более новый `updated_at` в списке важнее: это уже следующая запись.
 */
export function applyCommittedGeometry(
  geometries: Geometry[],
  objectId: number,
  committed: CommittedGeometryDetail | null,
): { list: Geometry[]; pending: boolean } {
  if (!committed || !sameEntityId(committed.objectId, objectId) || !committed.geometry?.geojson) {
    return { list: geometries, pending: false };
  }

  const savedId = Number(committed.geometry.id);
  if (!Number.isFinite(savedId)) {
    return { list: geometries, pending: false };
  }

  const saved: Geometry = {
    ...committed.geometry,
    id: savedId,
    object_id: Number(committed.geometry.object_id ?? committed.objectId),
  };
  const fromQuery = geometries.find((geometry) => sameEntityId(geometry.id, saved.id));
  const savedAt = timestamp(saved.updated_at);
  const queryAt = timestamp(fromQuery?.updated_at);

  if (fromQuery && queryAt != null && savedAt != null && queryAt > savedAt) {
    return { list: geometries, pending: false };
  }
  if (fromQuery && geoJsonKey(fromQuery.geojson) === geoJsonKey(saved.geojson)) {
    return { list: geometries, pending: false };
  }

  if (!fromQuery) {
    return { list: [...geometries, saved], pending: true };
  }
  return {
    list: geometries.map((geometry) =>
      sameEntityId(geometry.id, saved.id)
        ? { ...geometry, ...saved, geojson: saved.geojson }
        : geometry,
    ),
    pending: true,
  };
}

export async function purgeMvtRuntimeCache(): Promise<void> {
  if (typeof caches === "undefined" || typeof caches.delete !== "function") return;
  try {
    await caches.delete(MVT_RUNTIME_CACHE_NAME);
  } catch {
    /* Cache Storage недоступен (приватный режим, старый браузер). */
  }
}

type TileCache = {
  clearTiles?: () => void;
  update?: (transform: unknown) => void;
};

/** Сбрасывает уже декодированные тайлы источника, чтобы MapLibre запросил их заново. */
export function dropVectorTileCache(map: object, sourceId: string): void {
  const candidate = map as {
    style?: { sourceCaches?: Record<string, TileCache> };
    transform?: unknown;
    triggerRepaint?: () => void;
  };
  const cache = candidate.style?.sourceCaches?.[sourceId];
  if (!cache) return;
  try {
    cache.clearTiles?.();
    cache.update?.(candidate.transform);
    candidate.triggerRepaint?.();
  } catch {
    /* Кэш источника уже снят вместе со стилем. */
  }
}
